import { useEffect } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { Button, Card } from '@/shared/ui'

import { NetworkingConsent } from './NetworkingConsent'
import { NetworkingFields } from './NetworkingFields'
import { NetworkingTagPicker } from './NetworkingTagPicker'
import { useUpdateNetworkingDeclaration } from '../model/mutations'
import { useNetworkingDeclarationQuery, useNetworkingVocabulary } from '../model/queries'
import { useNetworkingAvailability } from '../model/useNetworkingAvailability'
import { emptyNetworkingForm, networkingFormSchema } from '../model/schemas'

import type { NetworkingFormValues } from '../model/types'


/**
 * Seção de networking do perfil do participante.
 *
 * Formulário próprio em vez do `Form` da tela de perfil: aquele está ligado ao
 * recurso `user` e salvaria no endpoint errado. Emails e Senha resolvem o mesmo
 * problema sobrescrevendo `onSubmit`; aqui a feature carrega o formulário inteiro,
 * o que também mantém o ponto de montagem fino.
 */
export const NetworkingSection = () => {
  const { digitalProductId } = useDigitalProductContext()
  const vocabulary = useNetworkingVocabulary()
  const { data: declaration } = useNetworkingDeclarationQuery(digitalProductId)
  const availability = useNetworkingAvailability()

  const declaredTags = declaration?.networking_tags ?? []

  const methods = useForm<NetworkingFormValues>({
    resolver: zodResolver(networkingFormSchema),
    defaultValues: emptyNetworkingForm
  })

  const {
    handleSubmit,
    reset,
    setError,
    formState: { isDirty }
  } = methods

  const mutation = useUpdateNetworkingDeclaration(digitalProductId, setError)

  useEffect(() => {
    if (!declaration) return

    reset({
      networking_opt_in: declaration.networking_opt_in,
      networking_tag_ids: declaration.networking_tag_ids ?? [],
      headline: declaration.networking_profile?.headline ?? '',
      company: declaration.networking_profile?.company ?? '',
      job_title: declaration.networking_profile?.job_title ?? ''
    })
  }, [declaration, reset])

  const onSubmit = async (values: NetworkingFormValues) => {
    try {
      await mutation.mutateAsync(values)
      toast.success('Preferências de networking salvas')
    } catch {
      // Erros de campo são aplicados por setError no onError da mutation. O toast
      // cobre o caso de falha sem detalhe por atributo.
      toast.error('Não foi possível salvar suas preferências de networking')
    }
  }

  // O evento sem vocabulário e sem nada declarado não tem networking configurado —
  // a seção inteira é omitida em vez de mostrar um formulário vazio. A condição
  // vive em `useNetworkingAvailability` porque a faixa de navegação e a guarda
  // de rota de `/minha-conta/networking` precisam da mesma resposta.
  if (!availability.isLoading && !availability.isAvailable) return null

  return (
    <Card>
      <Card.Body>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
            <div>
              <h3 className="m-0 text-base font-semibold text-gray-900">
                Networking
              </h3>
              <p className="mt-1 text-sm text-gray-700">
                Conte o que você busca neste evento para receber sugestões de
                pessoas, empresas e conteúdos com afinidade.
              </p>
            </div>

            <NetworkingConsent />

            <NetworkingTagPicker
              vocabulary={vocabulary}
              declaredTags={declaredTags}
            />

            <NetworkingFields />

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              loading={mutation.isLoading}
              disabled={!isDirty || mutation.isLoading}
              showTrailingIcon={false}
            >
              Salvar preferências
            </Button>
          </form>
        </FormProvider>
      </Card.Body>
    </Card>
  )
}
